import React from 'react'
import { useAdminDashboard } from '@/hooks/admin/useDashboard'
import { MetricCard } from '@/components/admin/MetricCard'
import {
  Store,
  DollarSign,
  TrendingUp,
  ShoppingCart,
  Users,
  AlertTriangle,
  Clock
} from 'lucide-react'
import { formatCurrency } from '@/utils/currency'
import type { RestaurantActivityDTO } from '@/types/admin'

export default function AdminDashboardPage() {
  const { data: dashboard, isLoading, error } = useAdminDashboard()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Platform Dashboard
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 animate-pulse"
            >
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Platform Dashboard
        </h1>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-600 dark:text-red-400">
            Errore nel caricamento dashboard: {error.message}
          </p>
        </div>
      </div>
    )
  }

  if (!dashboard) return null

  const { metrics, recentActivity, topPerformers, needsAttention } = dashboard

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Platform Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Overview completo della piattaforma multi-ristorante
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Restaurants */}
        <MetricCard
          title="Totale Ristoranti"
          value={metrics.totalRestaurants}
          subtitle={`${metrics.activeRestaurants} attivi • ${metrics.suspendedRestaurants} sospesi`}
          icon={<Store className="w-5 h-5" />}
          color="blue"
        />

        {/* MRR */}
        <MetricCard
          title="MRR (Monthly Recurring Revenue)"
          value={formatCurrency(metrics.monthlyRecurringRevenue)}
          subtitle={`ARR: ${formatCurrency(metrics.annualRecurringRevenue)}`}
          icon={<DollarSign className="w-5 h-5" />}
          color="green"
          trend={{
            value: metrics.revenueGrowthPercentage,
            isPositive: metrics.revenueGrowthPercentage >= 0
          }}
        />

        {/* Orders Today */}
        <MetricCard
          title="Ordini Oggi"
          value={metrics.totalOrdersToday}
          subtitle={`${metrics.totalOrdersThisMonth} questo mese`}
          icon={<ShoppingCart className="w-5 h-5" />}
          color="purple"
        />

        {/* Active Users */}
        <MetricCard
          title="Utenti Attivi"
          value={metrics.activeUsersToday}
          subtitle={`${metrics.totalUsers} totali`}
          icon={<Users className="w-5 h-5" />}
          color="orange"
        />
      </div>

      {/* Revenue Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          title="Revenue Questo Mese"
          value={formatCurrency(metrics.totalRevenueThisMonth)}
          subtitle="Subscription + Commissions"
          icon={<TrendingUp className="w-5 h-5" />}
          color="green"
        />

        <MetricCard
          title="Revenue Mese Scorso"
          value={formatCurrency(metrics.totalRevenueLastMonth)}
          subtitle="Confronto MoM"
          icon={<TrendingUp className="w-5 h-5" />}
          color="gray"
        />

        <MetricCard
          title="Volume Ordini Oggi"
          value={formatCurrency(metrics.totalOrderVolumeToday)}
          subtitle={`AVG: ${formatCurrency(metrics.averageOrderValue)}`}
          icon={<DollarSign className="w-5 h-5" />}
          color="blue"
        />
      </div>

      {/* Plan Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Distribuzione Piani
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-600 dark:text-gray-400">
              {metrics.freeRestaurants}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">FREE</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {metrics.starterRestaurants}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">STARTER</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {metrics.proRestaurants}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">PRO</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
              {metrics.enterpriseRestaurants}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">ENTERPRISE</div>
          </div>
        </div>
      </div>

      {/* Activity Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <RestaurantActivityTable
          title="Ristoranti Più Attivi"
          subtitle="Top 10 per ordini oggi"
          data={recentActivity}
          icon={<TrendingUp className="w-5 h-5" />}
          emptyMessage="Nessuna attività recente"
        />

        {/* Top Performers */}
        <RestaurantActivityTable
          title="Top Performers"
          subtitle="Top 10 per revenue questo mese"
          data={topPerformers}
          icon={<DollarSign className="w-5 h-5" />}
          emptyMessage="Nessun dato revenue"
        />
      </div>

      {/* Needs Attention */}
      {needsAttention.length > 0 && (
        <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <h2 className="text-lg font-semibold text-orange-900 dark:text-orange-200">
              Ristoranti che Richiedono Attenzione
            </h2>
          </div>
          <div className="space-y-3">
            {needsAttention.slice(0, 5).map((restaurant) => (
              <div
                key={restaurant.restaurantId}
                className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-4 border border-orange-200 dark:border-orange-800"
              >
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {restaurant.restaurantName}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {restaurant.status === 'SUSPENDED' ? (
                      <span className="text-red-600 dark:text-red-400">● Sospeso</span>
                    ) : (
                      <span className="text-orange-600 dark:text-orange-400">
                        ● Nessun ordine da 7+ giorni
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {restaurant.lastOrderAt ? (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(restaurant.lastOrderAt).toLocaleDateString('it-IT')}
                    </div>
                  ) : (
                    'Mai ordinato'
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        Ultimo aggiornamento: {new Date(metrics.calculatedAt).toLocaleString('it-IT')}
        <br />
        <span className="text-xs">Auto-refresh ogni 30 secondi</span>
      </div>
    </div>
  )
}

// Component for Restaurant Activity Tables
interface RestaurantActivityTableProps {
  title: string
  subtitle: string
  data: RestaurantActivityDTO[]
  icon: React.ReactNode
  emptyMessage: string
}

function RestaurantActivityTable({ title, subtitle, data, icon, emptyMessage }: RestaurantActivityTableProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
            {icon}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {data.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            {emptyMessage}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ristorante
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ordini
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.map((restaurant, index) => (
                <tr
                  key={restaurant.restaurantId}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        #{index + 1} {restaurant.restaurantName}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {restaurant.plan}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-900 dark:text-white font-medium">
                    {restaurant.ordersToday}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-900 dark:text-white font-medium">
                    {formatCurrency(restaurant.revenueThisMonth)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
